"use client";
import { Button } from "@/components/ui/Button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/Command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { Kbd, useShortcut } from "@/components/ui/Kbd";
import { Spinner } from "@/components/ui/Spinner";
import { FilterType } from "@/constants/filter";
import { useIsMobile } from "@/hooks/use-mobile";
import type { AnyFilterConfig, AnyLabeledFilterConfig } from "@/types/list";
import { Slot } from "@radix-ui/react-slot";
import { useForm, useStore } from "@tanstack/react-form";
import { Check, ChevronLeft, ChevronRight, ListFilterIcon } from "lucide-react";
import React, { createContext, useEffect } from "react";
import cn from "twsx";
import styles from "./FilterDropdown.module.css";
import type { ComboFilterOption } from "./components/FilterCombobox";
import { isFilterValueActive, mergeFilterValues } from "./filter-values";

export type FilterValue = any;
export type FilterFormValues = Record<string, any>;
export type FilterOption<T = string> = {
  icon?: React.ReactNode;
  label: string;
  defaultValue?: FilterValue;
  key: T extends object ? keyof T : string;
  type: FilterType;
  component: React.ComponentType<any>;
  options?: ComboFilterOption[];
  disableDate?: (date: Date) => boolean;
};

export type FilterForm = ReturnType<typeof useFilterForm>;
export type FilterOptionsResolvedHandler = (
  filterKey: string,
  options: ComboFilterOption[],
) => void;

export type FilterValueChange = (
  name: keyof FilterFormValues,
  value: FilterValue,
) => void;

export function useFilterForm(defaultValues: FilterFormValues) {
  return useForm({
    defaultValues,
  });
}

type FilterDropdownProps = {
  form?: FilterForm;
  currentValues?: FilterFormValues;
  listConfigFilters: Record<string, AnyFilterConfig>;
  filterComponents: Record<string, React.ComponentType<any>>;
  filterInput: Record<string, any>;
  isLoading?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  onChange?: FilterValueChange;
  onValuesChange?: (values: FilterFormValues) => void;
  onOptionsResolved?: FilterOptionsResolvedHandler;
  groupSearchResults?: boolean;
};

type StandaloneFilterDropdownProps =
  & Pick<
    FilterDropdownProps,
    "currentValues" | "filterInput" | "form" | "isLoading" | "disabled" | "children" | "className" | "onChange" | "onValuesChange" | "onOptionsResolved"
  >
  & {
    filterKey: string;
    filterConfig: AnyLabeledFilterConfig;
    filterComponent: FilterDropdownProps["filterComponents"][string];
  };

type FilterLabelHydratorProps = Pick<
  StandaloneFilterDropdownProps,
  "currentValues" | "filterConfig" | "filterComponent" | "filterInput" | "filterKey" | "onOptionsResolved"
>;

export const FilterProviderContext = createContext<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  openFilterValue: string | null;
  setOpenFilterValue: (
    value: string | null | ((prev: string | null) => string | null),
  ) => void;
  selectedItem: string | null;
  setSelectedItem: (value: string | null) => void;
}>({
  isOpen: false,
  setIsOpen: () => {},
  openFilterValue: null,
  setOpenFilterValue: () => {},
  selectedItem: null,
  setSelectedItem: () => {},
});

type FilterRuntimeContextValue = {
  filterKey: string;
  onOptionsResolved?: FilterOptionsResolvedHandler;
  requestClose?: () => void;
};

const FilterRuntimeContext = createContext<FilterRuntimeContextValue | null>(null);

export function useFilterRuntime() {
  return React.useContext(FilterRuntimeContext);
}

export function FilterProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [openFilterValue, setOpenFilterValue] = React.useState<string | null>(
    null,
  );
  const [selectedItem, setSelectedItem] = React.useState<string | null>(null);
  return (
    <FilterProviderContext.Provider
      value={{
        isOpen,
        setIsOpen,
        openFilterValue,
        setOpenFilterValue,
        selectedItem,
        setSelectedItem,
      }}
    >
      {children}
    </FilterProviderContext.Provider>
  );
}

export function useFilterProvider() {
  const context = React.useContext(FilterProviderContext);
  if (!context) {
    throw new Error("useFilterProvider must be used within a FilterProvider");
  }
  return context;
}

type FilterComponentProps = {
  as: React.ComponentType<any>;
  filterKey: string;
  filterConfig: AnyLabeledFilterConfig;
  filterInput: Record<string, any>;
  form: FilterForm;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange?: FilterValueChange;
  onOptionsResolved?: FilterOptionsResolvedHandler;
  requestClose?: () => void;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
};

function FilterComponent({
  as: Component,
  filterKey,
  filterConfig,
  filterInput,
  form,
  isOpen,
  onOpenChange,
  onChange,
  onOptionsResolved,
  requestClose,
  triggerRef,
}: FilterComponentProps) {
  if (filterConfig.label === null) return null;
  const filterProps = typeof filterConfig.props === "function"
    ? filterConfig.props(filterInput)
    : filterConfig.props;

  return (
    <FilterRuntimeContext.Provider
      value={{ filterKey, onOptionsResolved, requestClose }}
    >
      <React.Suspense
        fallback={
          <div className="flex min-h-8 items-center justify-center py-3">
            <Spinner size="md" tone="faint" />
          </div>
        }
      >
        <Component
          type={filterConfig.type}
          form={form}
          name={filterKey}
          label={filterConfig.label}
          triggerRef={triggerRef}
          onResolvedOptions={(options: ComboFilterOption[]) => {
            onOptionsResolved?.(filterKey, options);
          }}
          onChange={onChange}
          open={isOpen}
          onOpenChange={onOpenChange}
          {...filterProps}
        />
      </React.Suspense>
    </FilterRuntimeContext.Provider>
  );
}

type NestedFilterItemProps = {
  filterKey: string;
  filterConfig: AnyLabeledFilterConfig;
  filterComponent: React.ComponentType<any>;
  form: FilterForm;
  isOpen: boolean;
  isPrefetching: boolean;
  onOpenChange: () => void;
  onPrefetch: () => void;
  onChange?: FilterValueChange;
  setOpenFilterValue: (value: string | null) => void;
  filterInput: Record<string, any>;
  onOptionsResolved?: FilterOptionsResolvedHandler;
  requestClose?: () => void;
  inline?: boolean;
};

type FilterRadixSubmenuProps = Omit<NestedFilterItemProps, "filterConfig" | "form"> & {
  filterConfig: AnyLabeledFilterConfig;
  form: FilterForm;
  activeValueCount: number;
};

function FilterRadixSubmenu({
  filterKey,
  filterConfig,
  filterComponent,
  form,
  isOpen,
  isPrefetching,
  onOpenChange,
  onPrefetch,
  onChange,
  setOpenFilterValue,
  filterInput,
  onOptionsResolved,
  requestClose,
  activeValueCount,
  inline = false,
}: FilterRadixSubmenuProps) {
  const hoverOpenTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverOpenTimer = React.useCallback(() => {
    if (!hoverOpenTimerRef.current) return;
    clearTimeout(hoverOpenTimerRef.current);
    hoverOpenTimerRef.current = null;
  }, []);

  React.useEffect(() => clearHoverOpenTimer, [clearHoverOpenTimer]);

  const handlePointerEnter = (event: React.PointerEvent) => {
    if (event.pointerType !== "mouse") return;

    onPrefetch();
    clearHoverOpenTimer();
    hoverOpenTimerRef.current = setTimeout(() => {
      hoverOpenTimerRef.current = null;
      onOpenChange();
    }, 100);
  };

  const trigger = (
    <CommandItem
      value={filterKey}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={clearHoverOpenTimer}
      onSelect={onOpenChange}
      className="filter-item"
      keywords={[filterConfig.label]}
      data-filter-active={activeValueCount > 0 ? "true" : undefined}
    >
      {filterConfig.icon}
      <span className="min-w-0 flex-1 truncate">{filterConfig.label}</span>
      {activeValueCount > 0 && (
        <span className="filter-active-count" aria-label={`${activeValueCount} selected`}>
          {activeValueCount}
        </span>
      )}
      <ChevronRight className="filter-item-arrow" />
    </CommandItem>
  );

  if (inline) {
    return trigger;
  }

  return (
    <DropdownMenuSub
      key={filterKey}
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (open) {
          onOpenChange();
        }
      }}
    >
      <DropdownMenuSubTrigger asChild>
        {trigger}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        forceMount={isPrefetching || isOpen ? true : undefined}
        padding="none"
        className={cn("filter-menu-popover filter-menu-popover-formula data-[state=closed]:hidden", styles.submenu)}
        sideOffset={4}
        alignOffset={-4}
        collisionPadding={12}
        onPointerEnter={() => {
          onOpenChange();
        }}
        onEscapeKeyDown={() => {
          requestClose?.();
          setOpenFilterValue(null);
        }}
      >
        <FilterComponent
          as={filterComponent}
          filterKey={filterKey}
          filterConfig={filterConfig}
          filterInput={filterInput}
          form={form}
          isOpen={isOpen}
          onOpenChange={(open) => {
            if (!open) {
              setOpenFilterValue(null);
            }
          }}
          onChange={onChange}
          onOptionsResolved={onOptionsResolved}
          requestClose={requestClose}
        />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

type SearchableFilterOption = {
  filterKey: string;
  filterLabel: string;
  filterType: FilterType;
  option: SearchableOption;
};

type SearchableOption = {
  label: string;
  value: string;
};

function getSearchableFilterOptions(
  filterConfig: AnyLabeledFilterConfig,
  filterInput: Record<string, any>,
): SearchableOption[] {
  if (
    filterConfig.type !== FilterType.MULTI_SELECT
    && filterConfig.type !== FilterType.SELECT
  ) {
    return [];
  }

  const resolvedProps = typeof filterConfig.props === "function"
    ? filterConfig.props(filterInput)
    : filterConfig.props;

  const optionSource: Array<{
    label?: unknown;
    value?: unknown;
  }> = filterConfig.options
    ?? (Array.isArray(resolvedProps?.options) ? resolvedProps.options : []);

  return optionSource
    .filter((option: {
      label?: unknown;
      value?: unknown;
    }): option is SearchableOption => typeof option?.label === "string" && typeof option?.value === "string");
}

function NestedFilterItem({
  filterKey,
  filterConfig,
  filterComponent,
  form,
  isOpen,
  isPrefetching,
  onOpenChange,
  onPrefetch,
  onChange,
  setOpenFilterValue,
  filterInput,
  onOptionsResolved,
  requestClose,
  inline,
}: NestedFilterItemProps) {
  const labeledFilterConfig = filterConfig;
  const currentValue = useStore(form.store, (state) => state.values[filterKey]);
  const activeValueCount = isFilterValueActive(labeledFilterConfig, currentValue)
    ? (Array.isArray(currentValue) ? currentValue.length : 1)
    : 0;

  if (labeledFilterConfig.type === "input") {
    return (
      <CommandItem
        value={filterKey}
        onPointerEnter={() => {
          setOpenFilterValue(null);
        }}
        onSelect={() => {
          onOpenChange();
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight" || e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            onOpenChange();
          }
        }}
        className="filter-item"
        keywords={[labeledFilterConfig.label]}
        data-filter-active={activeValueCount > 0 ? "true" : undefined}
      >
        {labeledFilterConfig.icon}
        <span className="min-w-0 flex-1 truncate">{labeledFilterConfig.label}</span>
        {activeValueCount > 0 && (
          <>
            <span className="sr-only">Active</span>
            <Check aria-hidden="true" />
          </>
        )}
        <ChevronRight className="filter-item-arrow" />
      </CommandItem>
    );
  }

  return (
    <FilterRadixSubmenu
      key={filterKey}
      filterKey={filterKey}
      filterConfig={labeledFilterConfig}
      filterComponent={filterComponent}
      filterInput={filterInput}
      form={form}
      isOpen={isOpen}
      isPrefetching={isPrefetching}
      onOpenChange={onOpenChange}
      onPrefetch={onPrefetch}
      onChange={onChange}
      setOpenFilterValue={setOpenFilterValue}
      onOptionsResolved={onOptionsResolved}
      requestClose={requestClose}
      activeValueCount={activeValueCount}
      inline={inline}
    />
  );
}

type BooleanFilterItemProps = {
  filterKey: string;
  filterInput: Record<string, any>;
  filterConfig: AnyLabeledFilterConfig;
  form: FilterForm;
  onChange: FilterValueChange;
  setOpenFilterValue: (value: string | null) => void;
};

function BooleanFilterItem({
  filterKey,
  filterConfig,
  form,
  onChange,
  setOpenFilterValue,
}: BooleanFilterItemProps) {
  return (
    <form.Field name={filterKey} key={filterKey}>
      {(field: {
        state: { value: boolean };
        handleChange: (value: boolean) => void;
      }) => {
        const handleToggle = () => {
          const newValue = !field.state.value;
          field.handleChange(newValue);
          onChange(filterKey, newValue);
        };

        return (
          <CommandItem
            key={filterKey}
            value={filterKey}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "ArrowRight") {
                handleToggle();
              }
            }}
            onSelect={handleToggle}
            onPointerEnter={() => {
              setOpenFilterValue(null);
            }}
            className="filter-item"
            keywords={[filterConfig.label]}
            data-filter-active={field.state.value ? "true" : undefined}
          >
            {filterConfig.icon}
            <span className="min-w-0 flex-1 truncate">{filterConfig.label}</span>
            {field.state.value && (
              <>
                <span className="sr-only">Active</span>
                <Check aria-hidden="true" />
              </>
            )}
          </CommandItem>
        );
      }}
    </form.Field>
  );
}

export function FilterCommandMenu({
  currentValues,
  listConfigFilters,
  filterComponents,
  form,
  isLoading,
  disabled,
  children,
  className,
  onChange,
  onValuesChange,
  filterInput,
  onOptionsResolved,
  groupSearchResults = false,
}: FilterDropdownProps) {
  const {
    isOpen,
    setIsOpen,
    openFilterValue,
    setOpenFilterValue,
    selectedItem,
    setSelectedItem,
  } = useFilterProvider();
  const fallbackForm = useFilterForm(currentValues ?? {});
  const resolvedForm = form ?? fallbackForm;
  const isMobile = useIsMobile();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const commandInputRef = React.useRef<HTMLInputElement>(null);
  const [filterSearch, setFilterSearch] = React.useState("");
  const [prefetchFilterValue, setPrefetchFilterValue] = React.useState<string | null>(null);
  const [inputFilterKey, setInputFilterKey] = React.useState<string | null>(null);
  const inputFilterConfig = inputFilterKey ? listConfigFilters[inputFilterKey] : undefined;

  const requestClose = React.useCallback(() => {
    setIsOpen(false);
    setOpenFilterValue(null);
    setPrefetchFilterValue(null);
    setFilterSearch("");
  }, [setIsOpen, setOpenFilterValue]);

  useEffect(() => {
    if (!form && currentValues) {
      resolvedForm.reset(currentValues);
    }
  }, [currentValues, form, resolvedForm]);

  useEffect(() => {
    if (!isOpen) return;

    const focusFrame = window.requestAnimationFrame(() => {
      setOpenFilterValue(null);
      commandInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(focusFrame);
  }, [isOpen, setOpenFilterValue]);

  useEffect(() => {
    if (!isOpen) return;

    Object.values(listConfigFilters).forEach((filterConfig) => {
      if (
        filterConfig.label !== null
        && typeof filterConfig.component !== "string"
      ) {
        void filterConfig.component();
      }
    });
  }, [isOpen, listConfigFilters]);

  useShortcut("openFilters", (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  });

  const handleValueChange = React.useCallback<FilterValueChange>((name, value) => {
    onChange?.(name, value);
    onValuesChange?.(
      mergeFilterValues(
        currentValues,
        resolvedForm.state.values,
        name,
        value,
      ),
    );
  }, [
    currentValues,
    onChange,
    onValuesChange,
    resolvedForm.state.values,
  ]);

  const commandFilter = React.useCallback(
    (value: string, search: string, keywords?: string[]) => {
      if (!search) return 1;

      const normalizedSearch = search.toLowerCase().trim();
      if (!normalizedSearch) return 1;

      const haystack = `${value} ${keywords?.join(" ") ?? ""}`.toLowerCase();
      if (haystack === normalizedSearch) return 1;
      if (haystack.startsWith(normalizedSearch)) return 0.9;
      if (haystack.includes(normalizedSearch)) return 0.7;

      const searchTerms = normalizedSearch.split(/\s+/);
      const matchCount = searchTerms.filter((term) => haystack.includes(term)).length;

      return (matchCount / searchTerms.length) * 0.4;
    },
    [],
  );

  const searchableFilterOptions = React.useMemo<SearchableFilterOption[]>(
    () => {
      if (!filterSearch.trim()) return [];

      return Object.entries(listConfigFilters).flatMap(([filterKey, filterConfig]) => {
        if (filterConfig.label === null) return [];
        const labeledFilterConfig = filterConfig;

        return getSearchableFilterOptions(labeledFilterConfig, filterInput).map((option: SearchableOption) => ({
          filterKey,
          filterLabel: labeledFilterConfig.label,
          filterType: labeledFilterConfig.type,
          option,
        }));
      });
    },
    [filterInput, filterSearch, listConfigFilters],
  );

  const applyOptionShortcut = React.useCallback(
    (shortcut: SearchableFilterOption) => {
      const currentValue = resolvedForm.state.values[shortcut.filterKey];

      if (shortcut.filterType === FilterType.MULTI_SELECT) {
        const currentSelected = Array.isArray(currentValue) ? currentValue : [];
        if (currentSelected.includes(shortcut.option.value)) {
          return;
        }

        const nextValue = [...currentSelected, shortcut.option.value];
        resolvedForm.setFieldValue(shortcut.filterKey, nextValue);
        handleValueChange(shortcut.filterKey, nextValue);
        onOptionsResolved?.(shortcut.filterKey, [shortcut.option]);
        setFilterSearch("");
        return;
      }

      if (currentValue === shortcut.option.value) {
        return;
      }

      resolvedForm.setFieldValue(shortcut.filterKey, shortcut.option.value);
      handleValueChange(shortcut.filterKey, shortcut.option.value);
      onOptionsResolved?.(shortcut.filterKey, [shortcut.option]);
      setFilterSearch("");
    },
    [handleValueChange, onOptionsResolved, resolvedForm],
  );

  const isShortcutSelected = React.useCallback(
    (shortcut: SearchableFilterOption) => {
      const currentValue = resolvedForm.state.values[shortcut.filterKey];

      if (shortcut.filterType === FilterType.MULTI_SELECT) {
        return Array.isArray(currentValue) && currentValue.includes(shortcut.option.value);
      }

      return currentValue === shortcut.option.value;
    },
    [resolvedForm.state.values],
  );

  const nestedFilterKeys = React.useMemo(() => {
    return new Set(
      Object.entries(listConfigFilters)
        .filter(([, filterConfig]) => {
          return (
            filterConfig.label !== null
            && ([
              FilterType.MULTI_SELECT,
              FilterType.SELECT,
              FilterType.DATE,
              FilterType.INPUT,
            ] as FilterType[]).includes(filterConfig.type)
          );
        })
        .map(([filterKey]) => filterKey),
    );
  }, [listConfigFilters]);

  const mobileDrilldownConfig = React.useMemo(() => {
    if (!isMobile || !openFilterValue) return null;

    const config = listConfigFilters[openFilterValue];
    if (
      !config
      || config.label === null
      || config.type === FilterType.INPUT
    ) {
      return null;
    }

    return config;
  }, [isMobile, listConfigFilters, openFilterValue]);

  const returnToFilterList = React.useCallback(() => {
    setOpenFilterValue(null);
    setSelectedItem(null);
    window.requestAnimationFrame(() => commandInputRef.current?.focus());
  }, [setOpenFilterValue, setSelectedItem]);

  const renderSearchableOption = (shortcut: SearchableFilterOption) => {
    const isSelected = isShortcutSelected(shortcut);

    return (
      <CommandItem
        key={`${shortcut.filterKey}:${shortcut.option.value}`}
        value={`${shortcut.filterKey}:${shortcut.option.value}`}
        keywords={[
          shortcut.filterLabel,
          shortcut.option.label,
          `${shortcut.filterLabel} ${shortcut.option.label}`,
        ]}
        onSelect={() => {
          applyOptionShortcut(shortcut);
        }}
        onPointerEnter={() => {
          setOpenFilterValue(null);
        }}
        aria-pressed={isSelected}
        className="filter-option-shortcut"
      >
        <span className="min-w-0 truncate text-text-tertiary">
          {shortcut.filterLabel}
        </span>
        <span className="text-text-tertiary">/</span>
        <span className="min-w-0 flex-1 truncate">
          {shortcut.option.label}
        </span>
        <Check
          aria-hidden="true"
          className={isSelected ? "opacity-100" : "opacity-0"}
        />
      </CommandItem>
    );
  };

  return (
    <>
      <DropdownMenu
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          setSelectedItem(null);
          if (!open) {
            setOpenFilterValue(null);
            setPrefetchFilterValue(null);
            setFilterSearch("");
          }
        }}
        modal={true}
      >
        <DropdownMenuTrigger asChild ref={triggerRef}>
          {children ?? (
            <Button
              variant="tertiary"
              role="combobox"
              aria-expanded={isOpen}
              aria-label="Add filter"
              tooltip="Add filter"
              className={cn("group/filter-combobox", className)}
              disabled={disabled || isLoading}
            >
              <ListFilterIcon />
              Filter
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          padding="none"
          className={cn("filter-menu-popover filter-menu-root-popover", styles.rootMenu)}
          align="start"
          collisionPadding={12}
        >
          {mobileDrilldownConfig && openFilterValue
            ? (
              <div
                className="filter-mobile-drilldown"
                data-mobile-filter-drilldown={openFilterValue}
              >
                <button
                  type="button"
                  className="filter-mobile-drilldown-back"
                  aria-label={`Back to filters from ${mobileDrilldownConfig.label}`}
                  onClick={returnToFilterList}
                >
                  <ChevronLeft aria-hidden="true" />
                  <span>{mobileDrilldownConfig.label}</span>
                </button>
                <FilterComponent
                  as={filterComponents[openFilterValue] as React.ComponentType<any>}
                  filterKey={openFilterValue}
                  filterConfig={mobileDrilldownConfig}
                  filterInput={filterInput}
                  form={resolvedForm}
                  isOpen={true}
                  onOpenChange={(open) => {
                    if (!open) {
                      returnToFilterList();
                    }
                  }}
                  onChange={handleValueChange}
                  onOptionsResolved={onOptionsResolved}
                  requestClose={requestClose}
                />
              </div>
            )
            : (
              <Command
                className="filter-menu"
                filter={commandFilter}
                onValueChange={setSelectedItem}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpenFilterValue(null);
                  }
                  if (e.key === "ArrowRight") {
                    e.preventDefault();
                    e.stopPropagation();
                    if (selectedItem && nestedFilterKeys.has(selectedItem)) {
                      const selectedConfig = listConfigFilters[selectedItem];
                      if (selectedConfig?.label != null && selectedConfig.type === FilterType.INPUT) {
                        requestClose();
                        setInputFilterKey(selectedItem);
                      } else {
                        setOpenFilterValue(selectedItem);
                      }
                    }
                  }
                }}
              >
                <CommandInput
                  placeholder="Search filters…"
                  ref={commandInputRef}
                  value={filterSearch}
                  onValueChange={setFilterSearch}
                  hasBorder={false}
                  customAction={<Kbd shortcut="openFilters" />}
                />
                <CommandList className={cn("min-h-0 overscroll-contain", styles.drilldownList)}>
                  {isLoading
                    ? (
                      <div className="flex items-center justify-center py-6">
                        <Spinner size="md" tone="faint" role="status" aria-label="Loading filters" />
                      </div>
                    )
                    : (
                      <>
                        <CommandEmpty>No filters found.</CommandEmpty>
                        <CommandGroup
                          heading={groupSearchResults && filterSearch.trim() ? "Fields" : undefined}
                        >
                          {Object.entries(listConfigFilters).map(([filterKey, filterConfig]) => {
                            if (filterConfig.label === null) return null;

                            if (
                              ([
                                FilterType.MULTI_SELECT,
                                FilterType.SELECT,
                                FilterType.DATE,
                                FilterType.INPUT,
                              ] as FilterType[]).includes(filterConfig.type)
                            ) {
                              return (
                                <NestedFilterItem
                                  key={filterKey}
                                  filterKey={filterKey}
                                  filterConfig={filterConfig}
                                  filterComponent={filterComponents[filterKey] as React.ComponentType<any>}
                                  filterInput={filterInput}
                                  form={resolvedForm}
                                  isOpen={openFilterValue === filterKey}
                                  isPrefetching={prefetchFilterValue === filterKey}
                                  onOpenChange={() => {
                                    if (filterConfig.type === FilterType.INPUT) {
                                      requestClose();
                                      setInputFilterKey(filterKey);
                                    } else {
                                      setOpenFilterValue(filterKey);
                                    }
                                  }}
                                  onPrefetch={() => {
                                    setPrefetchFilterValue(filterKey);
                                  }}
                                  onChange={handleValueChange}
                                  setOpenFilterValue={setOpenFilterValue}
                                  onOptionsResolved={onOptionsResolved}
                                  requestClose={requestClose}
                                  inline={isMobile}
                                />
                              );
                            }

                            if (
                              filterConfig.type === FilterType.BOOLEAN
                              && resolvedForm
                              && onChange
                            ) {
                              return (
                                <BooleanFilterItem
                                  key={filterKey}
                                  filterKey={filterKey}
                                  filterConfig={filterConfig}
                                  filterInput={filterInput}
                                  form={resolvedForm}
                                  onChange={handleValueChange}
                                  setOpenFilterValue={setOpenFilterValue}
                                />
                              );
                            }

                            return null;
                          })}
                        </CommandGroup>
                        {searchableFilterOptions.length > 0 && groupSearchResults && <CommandSeparator />}
                        {searchableFilterOptions.length > 0 && (
                          <CommandGroup
                            heading={groupSearchResults ? "Values" : undefined}
                          >
                            {searchableFilterOptions.map(renderSearchableOption)}
                          </CommandGroup>
                        )}
                      </>
                    )}
                </CommandList>
              </Command>
            )}
        </DropdownMenuContent>
      </DropdownMenu>
      {inputFilterKey && inputFilterConfig?.label != null && (
        <FilterComponent
          as={filterComponents[inputFilterKey] as React.ComponentType<any>}
          filterKey={inputFilterKey}
          filterConfig={inputFilterConfig}
          filterInput={filterInput}
          form={resolvedForm}
          isOpen
          triggerRef={triggerRef}
          onOpenChange={(open) => {
            if (!open) setInputFilterKey(null);
          }}
          onChange={handleValueChange}
          onOptionsResolved={onOptionsResolved}
        />
      )}
    </>
  );
}

export function FilterDropdown(props: FilterDropdownProps) {
  const {
    currentValues,
    listConfigFilters,
    filterComponents,
    filterInput,
    form,
    isLoading,
    disabled,
    children,
    className,
    onChange,
    onValuesChange,
    onOptionsResolved,
    groupSearchResults,
  } = props;
  return (
    <FilterProvider>
      <FilterCommandMenu
        currentValues={currentValues}
        listConfigFilters={listConfigFilters}
        filterComponents={filterComponents}
        filterInput={filterInput}
        form={form}
        isLoading={isLoading}
        disabled={disabled}
        children={children}
        className={className}
        onChange={onChange}
        onValuesChange={onValuesChange}
        onOptionsResolved={onOptionsResolved}
        groupSearchResults={groupSearchResults}
      />
    </FilterProvider>
  );
}

function StandaloneFilterCommandMenu({
  currentValues,
  filterKey,
  filterConfig,
  filterComponent,
  filterInput,
  form,
  isLoading,
  disabled,
  children,
  className,
  onChange,
  onValuesChange,
  onOptionsResolved,
}: StandaloneFilterDropdownProps) {
  const {
    isOpen,
    setIsOpen,
    setOpenFilterValue,
  } = useFilterProvider();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const fallbackForm = useFilterForm(currentValues ?? {});
  const resolvedForm = form ?? fallbackForm;

  const requestClose = React.useCallback(() => {
    setIsOpen(false);
    setOpenFilterValue(null);
  }, [setIsOpen, setOpenFilterValue]);

  useEffect(() => {
    if (!form && currentValues) {
      resolvedForm.reset(currentValues);
    }
  }, [currentValues, form, resolvedForm]);

  const handleValueChange = React.useCallback<FilterValueChange>((name, value) => {
    onChange?.(name, value);
    onValuesChange?.(
      mergeFilterValues(
        currentValues,
        resolvedForm.state.values,
        name,
        value,
      ),
    );
  }, [
    currentValues,
    onChange,
    onValuesChange,
    resolvedForm.state.values,
  ]);

  const isInputFilter = filterConfig.type === FilterType.INPUT;

  const trigger = children ?? (
    <Button
      variant="secondary"
      role={isInputFilter ? undefined : "combobox"}
      aria-expanded={isOpen}
      aria-label={`Filter by ${filterConfig.label}`}
      tooltip={`Filter by ${filterConfig.label}`}
      className={cn("group/filter-combobox", className)}
      disabled={disabled || isLoading}
    >
      {filterConfig.icon}
      {filterConfig.label}
    </Button>
  );

  if (isInputFilter) {
    return (
      <>
        <Slot
          ref={triggerRef}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={() => {
            if (!disabled && !isLoading) setIsOpen(true);
          }}
        >
          {trigger}
        </Slot>
        <FilterComponent
          as={filterComponent}
          filterKey={filterKey}
          filterConfig={filterConfig}
          filterInput={filterInput}
          form={resolvedForm}
          isOpen={isOpen}
          triggerRef={triggerRef}
          onOpenChange={setIsOpen}
          onChange={handleValueChange}
          onOptionsResolved={onOptionsResolved}
        />
      </>
    );
  }

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setOpenFilterValue(null);
        }
      }}
      modal={true}
    >
      <DropdownMenuTrigger asChild ref={triggerRef}>
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        padding="none"
        className={cn("filter-menu-popover filter-menu-popover-formula overflow-visible", styles.standaloneMenu)}
        align="start"
      >
        {isLoading
          ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size="md" tone="faint" role="status" aria-label="Loading filter options" />
            </div>
          )
          : (
            <FilterComponent
              as={filterComponent}
              filterKey={filterKey}
              filterConfig={filterConfig}
              filterInput={filterInput}
              form={resolvedForm}
              isOpen={isOpen}
              onOpenChange={setIsOpen}
              onChange={handleValueChange}
              onOptionsResolved={onOptionsResolved}
              requestClose={requestClose}
            />
          )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function StandaloneFilterDropdown(props: StandaloneFilterDropdownProps) {
  return (
    <FilterProvider>
      <StandaloneFilterCommandMenu {...props} />
    </FilterProvider>
  );
}

function FilterLabelHydratorContent({
  currentValues,
  filterKey,
  filterConfig,
  filterComponent,
  filterInput,
  onOptionsResolved,
}: FilterLabelHydratorProps) {
  const form = useFilterForm(currentValues ?? {});

  return (
    <div hidden aria-hidden="true">
      <FilterComponent
        as={filterComponent}
        filterKey={filterKey}
        filterConfig={filterConfig}
        filterInput={filterInput}
        form={form}
        isOpen={false}
        onOpenChange={() => {}}
        onOptionsResolved={onOptionsResolved}
      />
    </div>
  );
}

export function FilterLabelHydrator(props: FilterLabelHydratorProps) {
  return (
    <FilterProvider>
      <FilterLabelHydratorContent {...props} />
    </FilterProvider>
  );
}
